import {formatNumber, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {ChangeEvent, useEffect} from 'react';

import {always, defaultTo, isNil} from 'ramda';
import {isNotNaN, isNotNil, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {Nullish, TestIdProps} from 'shared/web';

import {NEGATIVE_SIGN} from '../../../constants/numberConstants';
import {useUpdateCaret} from '../../../hooks/useUpdateCaret';
import {FormControlProps} from '../../../types/FormControlProps';
import {normalizeDecimalSeparator} from '../../../utils/normalizeDecimalSeparator';
import {replaceAlternativeNegativeSign} from '../../../utils/replaceAlternativeNegativeSign';
import {PlatformInputElement} from '../../TextInput/components/PlatformInputElement';
import {DEFAULT_STEP_VALUE} from '../constants/defaultStepValue';
import {getDecimalPlaces} from '../utils/getDecimalPlaces';
import {getFormattedInputNumber} from '../utils/getFormattedInputNumber';
import {getNumberFromString} from '../utils/getNumberFromString';

interface PlatformNumberInputElementProps
  extends FormControlProps<number | null, HTMLInputElement, [string | null]>, TestIdProps {
  /**
   * @about
   * The number of allowed decimal places
   */
  decimalPlaces?: number;
  /**
   * @about
   * Forces the input to always contain decimalPlaces specified in `decimalPlaces` prop.
   * @example
   * decimalPlaces={2}
   * forceDecimalPlaces={true}
   * // value is transformed `5` -> `5.00`
   */
  forceDecimalPlaces?: boolean;
  id: string;
  inputValue: string | Nullish;
  setInputValue: (value: string | Nullish) => void;
  step?: number;
  placeholder?: string;
  isFormatNumbersDisabled?: boolean;
  isAutoFocused?: boolean;
  // ARIA props
  role?: string;
  'aria-label'?: string;
  'aria-valuenow'?: number;
  'aria-valuemin'?: number;
  'aria-valuemax'?: number;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function PlatformNumberInputElement(props: PlatformNumberInputElementProps) {
  const {localeConfig} = useLocale();
  const numberLocale = localeConfig.number;
  const {inputRef, updateCaret} = useUpdateCaret(numberLocale.thousandsSeparator);

  useEffect(
    function syncInputValueWithValue() {
      const valueAsNumber = getNumberFromString(numberLocale, props.inputValue);

      if (isNotNil(props.inputValue) && valueAsNumber === props.value) {
        return;
      }

      // TODO: Remove Number casting - T20-24196
      const value = Number(props.value);

      const inputValue = match([
        isNotNilOrEmpty(props.value) && isNotNaN(value),
        isTrue(props.isFormatNumbersDisabled),
      ])
        .with([true, true], always(value.toString()))
        .with(
          [true, false],
          always(
            formatNumber(
              localeConfig,
              value,
              props.forceDecimalPlaces ? props.decimalPlaces : undefined
            )
          )
        )
        .otherwise(always(null));

      props.setInputValue(inputValue);

      // props object isn't in deps because it breaks negative numbers
    },
    // eslint-disable-next-line react/exhaustive-deps
    [localeConfig, numberLocale, props.value]
  );

  const handleChange = (value: string | null, event?: ChangeEvent<HTMLInputElement>) => {
    const normalizedValue = normalizeDecimalSeparator(
      replaceAlternativeNegativeSign(value),
      numberLocale,
      props.decimalPlaces
    );

    if (normalizedValue === NEGATIVE_SIGN || normalizedValue === '0-') {
      return props.setInputValue(NEGATIVE_SIGN);
    }

    const valueAsNumber = getNumberFromString(numberLocale, normalizedValue);
    const decimalPlaces = getDecimalPlaces(
      numberLocale.decimalSeparator,
      normalizedValue,
      props.step ?? DEFAULT_STEP_VALUE
    );

    if (
      isNaN(valueAsNumber) ||
      (!isNil(props.decimalPlaces) && decimalPlaces > props.decimalPlaces) ||
      valueAsNumber > Number.MAX_SAFE_INTEGER ||
      valueAsNumber < Number.MIN_SAFE_INTEGER
    ) {
      return;
    }

    const formattedValue = props.isFormatNumbersDisabled
      ? normalizedValue
      : getFormattedInputNumber(numberLocale, normalizedValue);

    props.setInputValue(formattedValue);
    props.onChange?.(normalizedValue ? valueAsNumber : null, event, formattedValue);
    updateCaret();
  };

  const isInvalid = props.isInvalid || !!props.errorMessage;

  return (
    <PlatformInputElement
      {...props}
      ref={inputRef}
      id={props.id}
      value={defaultTo(null, props.inputValue)}
      onChange={handleChange}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      isInvalid={isInvalid}
      isDisabled={!!props.isDisabled}
      isAutoFocused={props.isAutoFocused}
      placeholder={props.placeholder}
      data-name={props.name}
      data-testid={props['data-testid']}
    />
  );
}
